import { Button, useToast } from '@orbind/ui'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { CatalogIcon, ehUrlDeImagem } from '../../components/IconPicker.tsx'
import { ExplorerBody, useExplorer, useExplorerConfig, useExplorerRail, type ExplorerRailItem } from '../../shell/explorer.tsx'
import { AppFormModal } from './AppFormModal.tsx'
import { CredencialModal, ShareDialog } from './AppShareDialogs.tsx'
import { NOME_DO_SISTEMA, isDesktop, sistemaOperacional, urlDeDownload } from '../../lib/platform.ts'
import {
  buscarCredenciais,
  useApps,
  useCreateFolder,
  useDeleteApp,
  useDeleteFolder,
  useFolders,
  useMoveApp,
  useReorderApps,
  useReorderFolders,
  useUpdateFolder,
  type AppFolder,
  type UserApp,
} from './data.ts'
import { useConfirm } from '../work-item/bits.tsx'
import { desktopApps } from './desktop.ts'
import { useAppsRuntime } from './runtime.tsx'
import './apps.css'

/** Ultima aplicacao aberta — para voltar a ela ao reentrar no modulo. */
const CHAVE_ULTIMA = 'ob-apps-ultima'

/** Sigla de 2 letras para o mini-rail quando a aplicacao nao tem logo. */
function siglaDe(titulo: string): string {
  const palavras = titulo.trim().split(/\s+/).filter(Boolean)
  const primeira = palavras[0]
  if (!primeira) return '?'
  const segunda = palavras[1]
  if (!segunda) return primeira.slice(0, 2).toUpperCase()
  return `${primeira[0]}${segunda[0]}`.toUpperCase()
}

/** No mini-rail: a imagem/logo da aplicacao, ou a sigla quando nao ha logo. */
function conteudoDoRail(app: UserApp) {
  const temImagem = app.iconImageId !== null || (app.icon !== null && ehUrlDeImagem(app.icon ?? ''))
  if (temImagem) {
    const caminho = app.iconImageId ? `/public/catalog-icon/${app.iconImageId}` : app.icon
    return (
      <span style={app.color ? { color: app.color } : undefined} aria-hidden>
        <CatalogIcon icon={caminho} fallback="fas fa-globe" size={24} />
      </span>
    )
  }
  // sigla DIGITADA tem prioridade; senao deriva do titulo
  return <span aria-hidden>{app.abbreviation?.trim() || siglaDe(app.title)}</span>
}

/**
 * Modulo Aplicacoes.
 *
 * A lista vai para o explorer do shell (por portal) e a area de conteudo mostra
 * o cabecalho da aplicacao ativa. Os `<iframe>` em si nao sao renderizados
 * aqui — eles vivem no `AppsStage`, montado no shell, para sobreviverem a
 * navegacao entre modulos (ver `runtime.tsx`).
 */
export function AppsPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()

  const { data: apps, isPending, isError, error, refetch } = useApps()
  const runtime = useAppsRuntime()
  const remover = useDeleteApp()
  const moverApp = useMoveApp()

  const [formAberto, setFormAberto] = useState(false)
  const [editando, setEditando] = useState<UserApp | null>(null)
  // pasta onde a nova aplicacao deve nascer (botao "+" da pasta); null = raiz
  const [pastaAlvo, setPastaAlvo] = useState<string | null>(null)
  const [confirmando, setConfirmando] = useState<UserApp | null>(null)
  // compartilhar (dono) e credencial do recipiente (compartilhada)
  const [compartilhando, setCompartilhando] = useState<UserApp | null>(null)
  const [credencialDe, setCredencialDe] = useState<UserApp | null>(null)
  /** Saída de emergência quando a área de transferência recusa a cópia. */
  const [senhaVisivel, setSenhaVisivel] = useState<{
    app: UserApp
    username: string | null
    password: string
  } | null>(null)
  /** Fora do aplicativo instalado, abrir não é possível — ver runtime.tsx. */
  const noDesktop = isDesktop()

  // Qualquer dialogo aberto suspende a webview nativa: ela fica por cima do
  // DOM inteiro, e sem isso o formulario abriria invisivel atras do site
  // (tela aparentando travada — visto no desktop 0.2.0, 2026-08-25).
  const dialogoAberto = formAberto || confirmando !== null || senhaVisivel !== null
  const { suspender, adotar } = runtime
  useEffect(() => {
    suspender(dialogoAberto)
    return () => suspender(false)
  }, [dialogoAberto, suspender])

  // Readota webviews que sobreviveram a um reload da janela: elas foram
  // escondidas pelo runtime ao montar; aqui voltam a lista como "abertas"
  // (a sessao do site esta viva — clicar na linha so traz de volta).
  useEffect(() => {
    if (!noDesktop || !apps) return
    void desktopApps.abertas().then((ids) => {
      for (const id of ids) {
        const app = apps.find((a) => a.id === id)
        if (app) adotar(app)
      }
    })
  }, [noDesktop, apps, adotar])

  const abrirNova = () => {
    setEditando(null)
    setPastaAlvo(null)
    setFormAberto(true)
  }
  const abrirNovaNaPasta = (folderId: string) => {
    setEditando(null)
    setPastaAlvo(folderId)
    setFormAberto(true)
  }

  useExplorerConfig({
    footer: apps ? `${apps.length} ${apps.length === 1 ? 'aplicação' : 'aplicações'}` : null,
    onCreate: abrirNova,
  })

  const ativa = useMemo(() => apps?.find((a) => a.id === id) ?? null, [apps, id])

  // Entrar em /apps/:id abre a aplicacao; o runtime ignora se ja estiver aberta.
  useEffect(() => {
    if (!ativa) return
    if (ativa.openMode === 'tab') return
    runtime.abrir(ativa)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ativa?.id])

  // Guarda a ultima aplicacao aberta, para reabri-la ao voltar ao modulo.
  useEffect(() => {
    if (id) localStorage.setItem(CHAVE_ULTIMA, id)
  }, [id])

  // Reentrar em /apps (sem id) volta EXATAMENTE para a ultima aplicacao aberta,
  // com ela na area de trabalho — pedido 2026-08-26. So redireciona se ela
  // ainda existe; senao mostra as boas-vindas.
  useEffect(() => {
    if (id || !apps) return
    const ultima = localStorage.getItem(CHAVE_ULTIMA)
    if (ultima && apps.some((a) => a.id === ultima)) {
      navigate(`/apps/${ultima}`, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, apps])

  // Sair do modulo nao fecha nada, mas nenhuma aplicacao fica "visivel".
  useEffect(() => {
    if (!id) runtime.ativar(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  // Publica as aplicacoes no mini-rail (barra estreita quando o painel recolhe).
  const itensDoRail = useMemo<ExplorerRailItem[]>(
    () =>
      (apps ?? [])
        .filter((app) => app.openMode !== 'tab')
        .map((app) => ({
          id: app.id,
          label: app.title,
          content: conteudoDoRail(app),
          active: id === app.id,
          onClick: () => navigate(`/apps/${app.id}`),
        })),
    [apps, id, navigate],
  )
  useExplorerRail(itensDoRail)

  const escolher = (app: UserApp) => {
    if (app.openMode === 'tab') {
      window.open(app.url, '_blank', 'noopener,noreferrer')
      return
    }
    navigate(`/apps/${app.id}`)
  }

  const copiarCredenciais = async (app: UserApp) => {
    let credenciais: { username: string | null; password: string | null }
    try {
      credenciais = await buscarCredenciais(app.id)
    } catch (erro) {
      toast.push({ tone: 'danger', message: erro instanceof Error ? erro.message : 'Não foi possível ler a senha.' })
      return
    }
    if (!credenciais.password) {
      toast.push({ tone: 'warning', message: 'Nenhuma senha guardada para esta aplicação.' })
      return
    }
    try {
      await navigator.clipboard.writeText(credenciais.password)
      toast.push({
        tone: 'success',
        message: credenciais.username
          ? `Senha de ${credenciais.username} copiada.`
          : 'Senha copiada para a área de transferência.',
      })
    } catch {
      // A área de transferência recusa em vários casos legítimos (janela sem
      // foco, permissão negada, webview do desktop). Deixar o usuário sem a
      // senha depois de ele pedir por ela seria o pior desfecho — então ela
      // aparece para copiar à mão.
      setSenhaVisivel({ app, ...credenciais, password: credenciais.password })
    }
  }

  const confirmarRemocao = async () => {
    if (!confirmando) return
    const alvo = confirmando
    setConfirmando(null)
    try {
      await remover.mutateAsync(alvo.id)
      runtime.fechar(alvo.id)
      if (localStorage.getItem(CHAVE_ULTIMA) === alvo.id) localStorage.removeItem(CHAVE_ULTIMA)
      if (id === alvo.id) navigate('/apps', { replace: true })
      toast.push({ tone: 'success', message: `${alvo.title} removida.` })
    } catch (erro) {
      toast.push({ tone: 'danger', message: erro instanceof Error ? erro.message : 'Não foi possível remover.' })
    }
  }

  return (
    <>
      <ExplorerBody>
        <ListaDeAplicacoes
          apps={apps}
          carregando={isPending}
          erro={isError ? (error instanceof Error ? error.message : 'Falha ao carregar') : null}
          onRetry={() => void refetch()}
          ativaId={id ?? null}
          onEscolher={escolher}
          onEditar={(app) => {
            setEditando(app)
            setFormAberto(true)
          }}
          onCompartilhar={setCompartilhando}
          onCredencial={setCredencialDe}
          onRemover={setConfirmando}
          onFechar={(app) => {
            runtime.fechar(app.id)
            // power off = desligar de verdade: nao deve reabrir sozinha ao voltar
            if (localStorage.getItem(CHAVE_ULTIMA) === app.id) localStorage.removeItem(CHAVE_ULTIMA)
            if (id === app.id) navigate('/apps', { replace: true })
          }}
          onNova={abrirNova}
          onNovaNaPasta={abrirNovaNaPasta}
        />
      </ExplorerBody>

      {ativa ? (
        <AreaDaAplicacao
          app={ativa}
          todasApps={apps ?? []}
          noDesktop={noDesktop}
          onEditar={() => {
            setEditando(ativa)
            setFormAberto(true)
          }}
          onCopiarSenha={() => void copiarCredenciais(ativa)}
          onRecarregar={() => runtime.recarregar(ativa.id)}
        />
      ) : (
        <BoasVindas temAlguma={(apps?.length ?? 0) > 0} noDesktop={noDesktop} onNova={abrirNova} />
      )}

      <AppFormModal
        open={formAberto}
        editando={editando}
        onClose={() => setFormAberto(false)}
        onSaved={(app) => {
          runtime.sincronizar(app)
          if (!editando) {
            // criada pelo "+" de uma pasta: ja move pra dentro dela
            if (pastaAlvo) moverApp.mutate({ id: app.id, folderId: pastaAlvo })
            setPastaAlvo(null)
            navigate(`/apps/${app.id}`)
          }
        }}
      />

      {compartilhando ? <ShareDialog app={compartilhando} onClose={() => setCompartilhando(null)} /> : null}
      {credencialDe ? <CredencialModal app={credencialDe} onClose={() => setCredencialDe(null)} /> : null}

      {senhaVisivel ? (
        <div className="appconf" role="dialog" aria-modal="true" aria-label={`Acesso de ${senhaVisivel.app.title}`}>
          <div className="appconf__scrim" onClick={() => setSenhaVisivel(null)} aria-hidden />
          <div className="appconf__caixa">
            <h2 className="appconf__titulo">Acesso de {senhaVisivel.app.title}</h2>
            <p className="appconf__texto">
              O navegador não deixou copiar automaticamente. Selecione e copie à mão — a janela fecha quando você
              terminar.
            </p>
            <div className="appsenha">
              {senhaVisivel.username ? (
                <label className="appsenha__linha">
                  <span>Usuário</span>
                  <input readOnly value={senhaVisivel.username} onFocus={(e) => e.currentTarget.select()} />
                </label>
              ) : null}
              <label className="appsenha__linha">
                <span>Senha</span>
                <input
                  readOnly
                  autoFocus
                  value={senhaVisivel.password}
                  onFocus={(e) => e.currentTarget.select()}
                />
              </label>
            </div>
            <div className="appconf__acoes">
              <Button variant="primary" onClick={() => setSenhaVisivel(null)}>
                Fechar
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmando ? (
        <div className="appconf" role="dialog" aria-modal="true" aria-label={`Remover ${confirmando.title}`}>
          <div className="appconf__scrim" onClick={() => setConfirmando(null)} aria-hidden />
          <div className="appconf__caixa">
            <h2 className="appconf__titulo">Remover {confirmando.title}?</h2>
            <p className="appconf__texto">
              O atalho sai da sua área de trabalho e a senha guardada é apagada junto. Isso não afeta a sua conta no
              site — só o cadastro aqui.
            </p>
            <div className="appconf__acoes">
              <Button variant="ghost" onClick={() => setConfirmando(null)}>
                Cancelar
              </Button>
              <Button variant="danger" onClick={() => void confirmarRemocao()}>
                Remover
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}

/** Lista do explorer, com os quatro estados obrigatorios. */
function ListaDeAplicacoes({
  apps,
  carregando,
  erro,
  onRetry,
  ativaId,
  onEscolher,
  onEditar,
  onCompartilhar,
  onCredencial,
  onRemover,
  onFechar,
  onNova,
  onNovaNaPasta,
}: {
  apps: UserApp[] | undefined
  carregando: boolean
  erro: string | null
  onRetry: () => void
  ativaId: string | null
  onEscolher: (app: UserApp) => void
  onEditar: (app: UserApp) => void
  onCompartilhar: (app: UserApp) => void
  onCredencial: (app: UserApp) => void
  onRemover: (app: UserApp) => void
  onFechar: (app: UserApp) => void
  onNova: () => void
  onNovaNaPasta: (folderId: string) => void
}) {
  const { search } = useExplorer()
  const runtime = useAppsRuntime()
  const reordenar = useReorderApps()
  const [arrastando, setArrastando] = useState<{ tipo: 'app' | 'pasta'; id: string } | null>(null)
  // alvo de REORDER dentro de um nivel (mesma pasta): onde a app entra
  const [dropAlvo, setDropAlvo] = useState<{ parentId: string | null; ids: string[]; idx: number } | null>(null)
  // pastas
  const pastas = useFolders()
  const moverApp = useMoveApp()
  const criarPasta = useCreateFolder()
  const renomearPasta = useUpdateFolder()
  const apagarPasta = useDeleteFolder()
  const confirmPasta = useConfirm()
  const reordenarPasta = useReorderFolders()
  const [alvoPasta, setAlvoPasta] = useState<string | null | 'raiz'>(null)
  // linha azul de reorder de PASTAS num nivel: onde a pasta arrastada entra
  const [dropPastaLinha, setDropPastaLinha] = useState<{ parentId: string | null; ids: string[]; idx: number } | null>(null)
  const [renomeando, setRenomeando] = useState<{ id: string; texto: string } | null>(null)
  const [recolhidas, setRecolhidas] = useState<Set<string>>(() => {
    try {
      return new Set<string>(JSON.parse(localStorage.getItem('orbind.apps.pastas-recolhidas') || '[]'))
    } catch {
      return new Set<string>()
    }
  })
  const alternarRecolher = (id: string) =>
    setRecolhidas((atual) => {
      const prox = new Set(atual)
      if (prox.has(id)) prox.delete(id)
      else prox.add(id)
      localStorage.setItem('orbind.apps.pastas-recolhidas', JSON.stringify([...prox]))
      return prox
    })
  const gravarRecolhidas = (s: Set<string>) => {
    localStorage.setItem('orbind.apps.pastas-recolhidas', JSON.stringify([...s]))
    setRecolhidas(s)
  }
  // usar pastas (arvore) x sem pastas (lista plana)
  const [usarPastas, setUsarPastas] = useState(() => localStorage.getItem('orbind.apps.usar-pastas') !== '0')
  const alternarUsarPastas = () =>
    setUsarPastas((v) => {
      const novo = !v
      localStorage.setItem('orbind.apps.usar-pastas', novo ? '1' : '0')
      return novo
    })

  if (carregando) {
    return (
      <div className="applist">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="applist__skel" />
        ))}
      </div>
    )
  }

  if (erro) {
    return (
      <div className="applist__vazio">
        <i className="fas fa-exclamation-triangle" aria-hidden />
        <p>{erro}</p>
        <Button variant="ghost" onClick={onRetry}>
          Tentar de novo
        </Button>
      </div>
    )
  }

  const termo = search.trim().toLowerCase()
  const visiveis = (apps ?? []).filter(
    (a) =>
      termo === '' ||
      a.title.toLowerCase().includes(termo) ||
      a.url.toLowerCase().includes(termo) ||
      (a.description ?? '').toLowerCase().includes(termo),
  )

  const buscando = termo !== ''
  const foldersList = (pastas.data ?? [])
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'pt-BR'))

  const limparArraste = () => {
    setArrastando(null)
    setDropAlvo(null)
    setAlvoPasta(null)
    setDropPastaLinha(null)
  }
  // solta uma PASTA entre pastas de um nivel: reordena (e muda de pai se preciso)
  const soltarPastaNoNivel = () => {
    if (arrastando === null || arrastando.tipo !== 'pasta' || dropPastaLinha === null) return limparArraste()
    const alvoId = arrastando.id
    const { parentId, ids, idx } = dropPastaLinha
    if (!podeSoltarPastaEm(alvoId, parentId)) return limparArraste()
    const atual = foldersList.find((f) => f.id === alvoId)
    if (atual && (atual.parentFolderId ?? null) !== parentId) {
      renomearPasta.mutate({ id: alvoId, parentFolderId: parentId })
    }
    const base = ids.filter((id) => id !== alvoId)
    const destino = Math.min(idx, base.length)
    base.splice(destino, 0, alvoId)
    reordenarPasta.mutate(base)
    limparArraste()
  }
  // descendentes de uma pasta — para barrar mover a pasta para dentro de si/descendente
  const descendentesDaPasta = (id: string): Set<string> => {
    const filhosDe = new Map<string, string[]>()
    for (const f of foldersList) {
      if (f.parentFolderId) filhosDe.set(f.parentFolderId, [...(filhosDe.get(f.parentFolderId) ?? []), f.id])
    }
    const out = new Set<string>()
    const pilha = [id]
    while (pilha.length) {
      const cur = pilha.pop() as string
      for (const c of filhosDe.get(cur) ?? []) if (!out.has(c)) { out.add(c); pilha.push(c) }
    }
    return out
  }
  const podeSoltarPastaEm = (pastaId: string, alvo: string | null) =>
    alvo === null ? true : alvo !== pastaId && !descendentesDaPasta(pastaId).has(alvo)

  const soltarNaPasta = (folderId: string | null) => {
    if (arrastando) {
      if (arrastando.tipo === 'app') moverApp.mutate({ id: arrastando.id, folderId })
      else if (podeSoltarPastaEm(arrastando.id, folderId))
        renomearPasta.mutate({ id: arrastando.id, parentFolderId: folderId })
    }
    limparArraste()
  }
  // solta num ponto de um nivel: reordena (mesma pasta) e/ou move para a pasta do nivel (so app)
  const soltarNoNivel = () => {
    if (arrastando === null || dropAlvo === null || arrastando.tipo !== 'app') return limparArraste()
    const arrastadoId = arrastando.id
    const { parentId, ids, idx } = dropAlvo
    const arrastado = (apps ?? []).find((a) => a.id === arrastadoId)
    // mudou de pasta? move primeiro (a lista do nivel ainda nao tem essa app)
    if (arrastado && (arrastado.folderId ?? null) !== parentId) {
      moverApp.mutate({ id: arrastadoId, folderId: parentId })
    }
    const base = ids.filter((id) => id !== arrastadoId)
    const destino = Math.min(idx, base.length)
    base.splice(destino, 0, arrastadoId)
    reordenar.mutate(base)
    limparArraste()
  }

  if (visiveis.length === 0 && foldersList.length === 0) {
    return (
      <div className="applist__vazio">
        <i className="fas fa-th-large" aria-hidden />
        {termo === '' ? (
          <>
            <p>Nenhuma aplicação por aqui ainda.</p>
            <p className="applist__vazio-dica">
              Adicione os sites que você mais usa e eles ficam a um clique, dentro do Orbind.
            </p>
            <Button variant="primary" onClick={onNova}>
              Adicionar aplicação
            </Button>
          </>
        ) : (
          <p>Nenhuma aplicação encontrada para “{search}”.</p>
        )}
      </div>
    )
  }

  const linhaApp = (app: UserApp, ctx?: { parentId: string | null; ids: string[]; idx: number }) => {
    const aberta = runtime.estaAberta(app.id)
    const marcar =
      ctx !== undefined && dropAlvo !== null && dropAlvo.parentId === ctx.parentId && dropAlvo.idx === ctx.idx
    return (
      <Fragment key={app.id}>
        {marcar ? <div className="applist__drop" aria-hidden /> : null}
        <div
        className={`applist__item${ativaId === app.id ? ' applist__item--ativo' : ''}${
          arrastando?.tipo === 'app' && arrastando.id === app.id ? ' applist__item--arrastando' : ''
        }`}
        draggable={!buscando && app.isOwner}
        onDragStart={(e) => {
          if (buscando || !app.isOwner) return
          e.dataTransfer.effectAllowed = 'move'
          setArrastando({ tipo: 'app', id: app.id })
        }}
        onDragOver={(e) => {
          // reorder entre apps so quando arrastando uma APP
          if (arrastando?.tipo !== 'app' || ctx === undefined) return
          e.preventDefault()
          e.stopPropagation()
          const r = e.currentTarget.getBoundingClientRect()
          const depois = e.clientY >= r.top + r.height / 2
          setAlvoPasta(null)
          setDropAlvo({ parentId: ctx.parentId, ids: ctx.ids, idx: ctx.idx + (depois ? 1 : 0) })
        }}
        onDrop={(e) => {
          if (ctx === undefined) return
          e.preventDefault()
          e.stopPropagation()
          soltarNoNivel()
        }}
        onDragEnd={limparArraste}
      >
            <button type="button" className="applist__botao" onClick={() => onEscolher(app)} title={app.url}>
              <span className="applist__icone" style={app.color ? { color: app.color } : undefined} aria-hidden>
                <CatalogIcon
                  icon={app.iconImageId ? `/public/catalog-icon/${app.iconImageId}` : app.icon}
                  fallback="fas fa-globe"
                  size={18}
                  fill
                />
              </span>
              <span className="applist__texto">
                <span className="applist__nome">{app.title}</span>
                <span className="applist__url">
                  {hostDe(app.url)}
                  {app.shared ? <span className="applist__tag">compartilhada</span> : null}
                </span>
              </span>
              {aberta ? (
                <span className="applist__aberta" title="Aplicação aberta">
                  <span className="applist__ponto" aria-hidden />
                  <span className="applist__aberta-txt">aberta</span>
                </span>
              ) : null}
            </button>

            <div className="applist__acoes">
              {aberta ? (
                <button
                  type="button"
                  className="applist__acao"
                  aria-label={`Fechar ${app.title}`}
                  title="Fechar a aplicação de verdade"
                  onClick={() => onFechar(app)}
                >
                  <i className="fas fa-power-off" aria-hidden />
                </button>
              ) : null}
              {app.isOwner ? (
                <>
                  <button
                    type="button"
                    className="applist__acao"
                    aria-label={`Editar ${app.title}`}
                    title="Editar"
                    onClick={() => onEditar(app)}
                  >
                    <i className="fas fa-pen" aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="applist__acao"
                    aria-label={`Compartilhar ${app.title}`}
                    title={app.sharedEveryone ? 'Compartilhada com todos' : 'Compartilhar'}
                    onClick={() => onCompartilhar(app)}
                  >
                    <i className="fas fa-share-alt" aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="applist__acao applist__acao--perigo"
                    aria-label={`Remover ${app.title}`}
                    title="Remover"
                    onClick={() => onRemover(app)}
                  >
                    <i className="fas fa-trash" aria-hidden />
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="applist__acao"
                  aria-label={`Minhas credenciais de ${app.title}`}
                  title="Compartilhada com você — definir seu usuário e senha"
                  onClick={() => onCredencial(app)}
                >
                  <i className="fas fa-key" aria-hidden />
                </button>
              )}
            </div>
          </div>
        </Fragment>
    )
  }

  // renderiza um nivel da arvore: subpastas (recursivo) + aplicacoes da pasta
  const renderNivel = (parentId: string | null, prof: number) => {
    const subpastas = foldersList.filter((f) => (f.parentFolderId ?? null) === parentId)
    const appsAqui = visiveis
      .filter((a) => (a.folderId ?? null) === parentId)
      .slice()
      .sort((x, y) => x.sortOrder - y.sortOrder)
    const idsNivel = appsAqui.map((a) => a.id)
    const subIds = subpastas.map((s) => s.id)
    const linhaAntes = (i: number) =>
      arrastando?.tipo === 'pasta' && dropPastaLinha?.parentId === parentId && dropPastaLinha.idx === i
    return (
      <>
        {subpastas.map((f, i) => {
          const recolhida = recolhidas.has(f.id)
          const editando = renomeando?.id === f.id
          return (
            <Fragment key={f.id}>
              {linhaAntes(i) ? <div className="applist__drop" aria-hidden style={{ marginLeft: prof * 14 }} /> : null}
              <div>
              <div
                className={`applist__pasta${alvoPasta === f.id ? ' applist__pasta--alvo' : ''}${
                  arrastando?.tipo === 'pasta' && arrastando.id === f.id ? ' applist__pasta--arrastando' : ''
                }`}
                style={{ paddingLeft: 6 + prof * 14 }}
                draggable={f.owner && !editando}
                onDragStart={(e) => {
                  if (!f.owner) return
                  e.dataTransfer.effectAllowed = 'move'
                  e.stopPropagation()
                  setArrastando({ tipo: 'pasta', id: f.id })
                }}
                onDragOver={(e) => {
                  if (arrastando === null || !f.owner) return
                  e.preventDefault()
                  if (arrastando.tipo === 'pasta') {
                    // banda de cima/baixo = reordenar (linha azul); meio = entrar na pasta
                    const r = e.currentTarget.getBoundingClientRect()
                    const y = e.clientY - r.top
                    const banda = r.height * 0.28
                    if (y < banda) {
                      setAlvoPasta(null)
                      setDropPastaLinha({ parentId, ids: subIds, idx: i })
                    } else if (y > r.height - banda) {
                      setAlvoPasta(null)
                      setDropPastaLinha({ parentId, ids: subIds, idx: i + 1 })
                    } else if (podeSoltarPastaEm(arrastando.id, f.id)) {
                      setDropPastaLinha(null)
                      setAlvoPasta(f.id)
                    } else {
                      setAlvoPasta(null)
                      setDropPastaLinha(null)
                    }
                  } else {
                    // arrastando uma APP: sempre entra na pasta
                    setDropAlvo(null)
                    setDropPastaLinha(null)
                    setAlvoPasta(f.id)
                  }
                }}
                onDragLeave={(e) => {
                  if (e.currentTarget.contains(e.relatedTarget as Node)) return
                  setAlvoPasta((a) => (a === f.id ? null : a))
                }}
                onDrop={(e) => {
                  if (!f.owner) return
                  e.preventDefault()
                  e.stopPropagation()
                  if (dropPastaLinha) soltarPastaNoNivel()
                  else soltarNaPasta(f.id)
                }}
                onDragEnd={limparArraste}
              >
                <button
                  type="button"
                  className="applist__pasta-tgl"
                  aria-label={recolhida ? 'Expandir pasta' : 'Recolher pasta'}
                  onClick={() => alternarRecolher(f.id)}
                >
                  <i className={`fas fa-chevron-${recolhida ? 'right' : 'down'}`} aria-hidden />
                </button>
                <i className={`fas fa-folder${recolhida ? '' : '-open'} applist__pasta-ico`} aria-hidden />
                {editando ? (
                  <input
                    className="applist__pasta-input"
                    autoFocus
                    value={renomeando?.texto ?? ''}
                    onChange={(e) => setRenomeando({ id: f.id, texto: e.target.value })}
                    onBlur={() => {
                      const t = (renomeando?.texto ?? '').trim()
                      if (t && t !== f.name) renomearPasta.mutate({ id: f.id, name: t })
                      setRenomeando(null)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') e.currentTarget.blur()
                      if (e.key === 'Escape') setRenomeando(null)
                    }}
                  />
                ) : (
                  <button
                    type="button"
                    className="applist__pasta-nome"
                    title={f.owner ? 'Um clique recolhe · dois cliques renomeia' : 'Pasta compartilhada com você'}
                    onDoubleClick={() => f.owner && setRenomeando({ id: f.id, texto: f.name })}
                    onClick={() => alternarRecolher(f.id)}
                  >
                    {f.name}
                  </button>
                )}
                {!f.owner ? <span className="applist__tag">compartilhada</span> : null}
                <span style={{ flex: 1 }} />
                {f.owner ? (
                  <span className="applist__pasta-acoes">
                    <button
                      type="button"
                      className="applist__acao"
                      aria-label="Adicionar aplicação nesta pasta"
                      title="Adicionar aplicação nesta pasta"
                      onClick={() => onNovaNaPasta(f.id)}
                    >
                      <i className="fas fa-plus" aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="applist__acao"
                      aria-label="Nova subpasta"
                      title="Nova subpasta"
                      onClick={() => criarPasta.mutate({ name: 'Nova pasta', parentFolderId: f.id })}
                    >
                      <i className="fas fa-folder-plus" aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="applist__acao applist__acao--perigo"
                      aria-label="Apagar pasta"
                      title="Apagar pasta (as apps e subpastas sobem um nível)"
                      onClick={() =>
                        confirmPasta.ask(
                          `A pasta "${f.name}" será apagada. As aplicações e subpastas dela sobem um nível.`,
                          () => apagarPasta.mutate(f.id),
                        )
                      }
                    >
                      <i className="fas fa-trash" aria-hidden />
                    </button>
                  </span>
                ) : null}
              </div>
              {recolhida ? null : <div>{renderNivel(f.id, prof + 1)}</div>}
            </div>
            </Fragment>
          )
        })}
        {arrastando?.tipo === 'pasta' &&
        dropPastaLinha?.parentId === parentId &&
        dropPastaLinha.idx === subpastas.length ? (
          <div className="applist__drop" aria-hidden style={{ marginLeft: prof * 14 }} />
        ) : null}
        <div style={{ paddingLeft: prof * 14 }}>
          {appsAqui.map((app, i) => linhaApp(app, { parentId, ids: idsNivel, idx: i }))}
          {arrastando !== null && dropAlvo?.parentId === parentId && dropAlvo.idx === appsAqui.length ? (
            <div className="applist__drop" aria-hidden />
          ) : null}
        </div>
      </>
    )
  }

  return (
    <div
      className="applist"
      onDragOver={(e) => {
        if (arrastando !== null) e.preventDefault()
      }}
      onDrop={(e) => {
        e.preventDefault()
        soltarNaPasta(null)
      }}
    >
      {runtime.abertas.length > 0 ? (
        <div className="applist__abertos" aria-label="Aplicações abertas">
          <span className="applist__abertos-tit">Abertas</span>
          {runtime.abertas.map((a) => (
            // a linha é um par de botões: o da aplicação e o de desligar — por
            // isso a linha virou um contêiner, e não um botão só (2026-08-31)
            <div key={a.id} className={`applist__abertolinha${ativaId === a.id ? ' applist__abertolinha--on' : ''}`}>
              <button
                type="button"
                className="applist__aberto"
                title={a.title}
                onClick={() => onEscolher(a)}
              >
                <span className="applist__aberto-ico" style={a.color ? { color: a.color } : undefined} aria-hidden>
                  <CatalogIcon
                    icon={a.iconImageId ? `/public/catalog-icon/${a.iconImageId}` : a.icon}
                    fallback="fas fa-globe"
                    size={14}
                    fill
                  />
                </span>
                <span className="applist__aberto-nome">{a.title}</span>
              </button>
              <button
                type="button"
                className="applist__abertox"
                title={`Desligar ${a.title}`}
                aria-label={`Desligar ${a.title}`}
                onClick={() => runtime.fechar(a.id)}
              >
                <i className="fas fa-power-off" aria-hidden />
              </button>
            </div>
          ))}
        </div>
      ) : null}
      {!buscando ? (
        <div className="applist__ferramentas">
          {usarPastas ? (
            <>
              <button
                type="button"
                className="applist__novapasta"
                onClick={() => criarPasta.mutate({ name: 'Nova pasta' })}
              >
                <i className="fas fa-folder-plus" aria-hidden /> Nova pasta
              </button>
              <button
                type="button"
                className="applist__ferrbtn"
                aria-label="Expandir todas as pastas"
                title="Expandir todas"
                onClick={() => gravarRecolhidas(new Set())}
              >
                <i className="fas fa-angle-double-down" aria-hidden />
              </button>
              <button
                type="button"
                className="applist__ferrbtn"
                aria-label="Recolher todas as pastas"
                title="Recolher todas"
                onClick={() => gravarRecolhidas(new Set((pastas.data ?? []).map((f) => f.id)))}
              >
                <i className="fas fa-angle-double-up" aria-hidden />
              </button>
            </>
          ) : null}
          <span style={{ flex: 1 }} />
          <button
            type="button"
            className="applist__ferrbtn"
            aria-label={usarPastas ? 'Sem pastas (lista simples)' : 'Usar pastas'}
            title={usarPastas ? 'Sem pastas — lista simples' : 'Usar pastas'}
            onClick={alternarUsarPastas}
          >
            <i className={usarPastas ? 'fas fa-list' : 'fas fa-folder'} aria-hidden />
          </button>
        </div>
      ) : null}
      {buscando || !usarPastas ? visiveis.map((app) => linhaApp(app)) : renderNivel(null, 0)}
      {confirmPasta.element}
    </div>
  )
}

/**
 * Cabecalho da aplicacao ativa e, na web, o convite ao aplicativo.
 *
 * O quadro da aplicacao fica no `AppsStage`; esta area desenha por cima dele a
 * barra de titulo, e o palco tem um recuo no topo para nao ficar por baixo.
 */
function AreaDaAplicacao({
  app,
  todasApps,
  noDesktop,
  onEditar,
  onCopiarSenha,
  onRecarregar,
}: {
  app: UserApp
  todasApps: UserApp[]
  noDesktop: boolean
  onEditar: () => void
  onCopiarSenha: () => void
  onRecarregar: () => void
}) {
  const { ativaId, abrir, telaCheia, alternarTelaCheia } = useAppsRuntime()
  // Preferencia da barra em tela cheia: so o icone (padrao) ou icone + nome.
  const [abasComNome, setAbasComNome] = useState(() => localStorage.getItem('orbind.apps.abas-nome') === '1')
  const alternarAbasComNome = () =>
    setAbasComNome((v) => {
      const novo = !v
      localStorage.setItem('orbind.apps.abas-nome', novo ? '1' : '0')
      return novo
    })
  return (
    <div className={`apparea${noDesktop ? '' : ' apparea--bloqueado'}`}>
      <header className={`apparea__barra${telaCheia ? ' apparea__barra--cheia' : ''}`}>
        {telaCheia ? (
          // tela cheia: TODAS as aplicacoes viram quadradinhos (so o icone, com o
          // favicon como padrao) na barra do topo — clicar alterna/abre a app.
          <div
            className={`apparea__abas${abasComNome ? ' apparea__abas--nomes' : ''}`}
            role="tablist"
            aria-label="Aplicações"
          >
            {todasApps.map((a) => (
              <button
                key={a.id}
                type="button"
                role="tab"
                aria-selected={a.id === ativaId}
                className={`apparea__aba${a.id === ativaId ? ' apparea__aba--on' : ''}`}
                title={a.title}
                onClick={() => abrir(a)}
              >
                <span className="apparea__aba-icone" style={a.color ? { color: a.color } : undefined} aria-hidden>
                  <CatalogIcon
                    icon={a.iconImageId ? `/public/catalog-icon/${a.iconImageId}` : a.icon}
                    fallback="fas fa-globe"
                    size={20}
                    fill
                  />
                </span>
                {abasComNome ? <span className="apparea__aba-nome">{a.abbreviation?.trim() || a.title}</span> : null}
              </button>
            ))}
          </div>
        ) : (
          <>
            <span className="apparea__icone" style={app.color ? { color: app.color } : undefined} aria-hidden>
              <CatalogIcon
                icon={app.iconImageId ? `/public/catalog-icon/${app.iconImageId}` : app.icon}
                fallback="fas fa-globe"
                size={16}
              />
            </span>
            <h1 className="apparea__titulo">{app.title}</h1>
            <span className="apparea__host">{hostDe(app.url)}</span>
          </>
        )}

        <div className="apparea__acoes">
          {noDesktop ? (
            <button type="button" className="apparea__acao" title="Recarregar a aplicação" onClick={onRecarregar}>
              <i className="fas fa-redo" aria-hidden />
            </button>
          ) : null}
          {/* downloads feitos DENTRO das aplicacoes: painel com a lista
              (arquivo, aplicacao, status), abrir e revelar (2026-09-01) */}
          {noDesktop ? <DownloadsBotao apps={todasApps} /> : null}
          {app.hasCredentials ? (
            <button type="button" className="apparea__acao" title="Copiar a senha guardada" onClick={onCopiarSenha}>
              <i className="fas fa-key" aria-hidden />
              <span>Copiar senha</span>
            </button>
          ) : null}
          <button
            type="button"
            className="apparea__acao"
            title={noDesktop ? 'Abrir no navegador do sistema' : 'Abrir em nova aba do navegador'}
            onClick={() => window.open(app.url, '_blank', 'noopener,noreferrer')}
          >
            <i className="fas fa-external-link-alt" aria-hidden />
            <span>{noDesktop ? 'Navegador' : 'Nova aba'}</span>
          </button>
          <button type="button" className="apparea__acao" title="Editar cadastro" onClick={onEditar}>
            <i className="fas fa-pen" aria-hidden />
          </button>
          {/* so em tela cheia: alterna a barra de apps entre so-icone e icone+nome */}
          {telaCheia ? (
            <button
              type="button"
              className="apparea__acao"
              aria-label={abasComNome ? 'Mostrar só os ícones' : 'Mostrar os nomes'}
              title={abasComNome ? 'Mostrar só os ícones' : 'Mostrar os nomes'}
              onClick={alternarAbasComNome}
            >
              <i className={abasComNome ? 'fas fa-th-large' : 'fas fa-font'} aria-hidden />
            </button>
          ) : null}
          {/* tela cheia: some as barras laterais; os apps viram abas aqui em cima.
              So faz sentido no desktop, onde a aplicacao abre de verdade (webview
              nativa); na web a area fica bloqueada, entao nao mostra o botao. */}
          {noDesktop ? (
            <button
              type="button"
              className="apparea__acao"
              aria-label={telaCheia ? 'Voltar à tela normal' : 'Expandir para a tela toda'}
              title={telaCheia ? 'Tela normal' : 'Tela toda'}
              onClick={alternarTelaCheia}
            >
              <i className={telaCheia ? 'fas fa-compress' : 'fas fa-expand'} aria-hidden />
            </button>
          ) : null}
        </div>
      </header>

      {noDesktop ? null : <ConviteAoDesktop app={app} onCopiarSenha={onCopiarSenha} />}
    </div>
  )
}

/**
 * Botão + painel de downloads das aplicações (2026-09-01).
 *
 * O shell nativo intercepta cada download das webviews, salva na pasta
 * Downloads do Windows e registra; aqui a lista aparece com a aplicação de
 * origem e o status. Clicar no nome abre o arquivo; a pastinha revela no
 * Explorer. Enquanto o painel está aberto, a lista se atualiza sozinha.
 */
function DownloadsBotao({ apps }: { apps: UserApp[] }) {
  const [aberto, setAberto] = useState(false)
  // a webview NATIVA fica por cima do DOM: com o painel aberto ela e
  // suspensa, como os demais dialogos desta tela fazem
  const { suspender } = useAppsRuntime()
  useEffect(() => {
    suspender(aberto)
    return () => suspender(false)
  }, [aberto, suspender])
  const [lista, setLista] = useState<
    { id: number; app: string; arquivo: string; caminho: string; status: string; quando_ms: number }[]
  >([])

  useEffect(() => {
    if (!aberto) return
    let vivo = true
    const carregar = () => {
      void desktopApps.listarDownloads().then((d) => {
        if (vivo) setLista(d)
      })
    }
    carregar()
    const timer = window.setInterval(carregar, 2000)
    return () => {
      vivo = false
      window.clearInterval(timer)
    }
  }, [aberto])

  const nomeDaApp = (id: string) => apps.find((a) => a.id === id)?.title ?? 'Aplicação'
  const hora = (ms: number) =>
    new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(new Date(ms))
  const baixando = lista.filter((d) => d.status === 'baixando').length

  return (
    <span className="appdl">
      <button
        type="button"
        className="apparea__acao"
        title="Downloads das aplicações"
        aria-label="Downloads das aplicações"
        aria-expanded={aberto}
        onClick={() => setAberto((v) => !v)}
      >
        <i className="fas fa-download" aria-hidden />
        {baixando > 0 ? <span className="appdl__andamento" aria-hidden /> : null}
      </button>
      {aberto ? (
        <>
          <div className="wi-menu__backdrop" onClick={() => setAberto(false)} />
          <div className="appdl__painel" role="dialog" aria-label="Downloads">
            <div className="appdl__cab">
              <span className="appdl__titulo">Downloads</span>
              <button
                type="button"
                className="appdl__pasta"
                title="Abrir a pasta de Downloads do computador"
                onClick={() => void desktopApps.abrirDownloads()}
              >
                <i className="fas fa-folder-open" aria-hidden /> Abrir pasta
              </button>
            </div>
            {lista.length === 0 ? (
              <p className="appdl__vazio">
                Nenhum download por aqui ainda. Baixe um arquivo dentro de uma aplicação e ele aparece nesta lista.
              </p>
            ) : (
              <div className="appdl__lista">
                {lista.map((d) => (
                  <div key={d.id} className="appdl__linha">
                    <i
                      className={
                        d.status === 'baixando'
                          ? 'fas fa-spinner fa-spin appdl__ico'
                          : d.status === 'ok'
                            ? 'fas fa-check-circle appdl__ico appdl__ico--ok'
                            : 'fas fa-exclamation-circle appdl__ico appdl__ico--erro'
                      }
                      aria-hidden
                    />
                    <button
                      type="button"
                      className="appdl__arquivo"
                      title={d.status === 'ok' ? `Abrir ${d.arquivo}` : d.arquivo}
                      disabled={d.status !== 'ok'}
                      onClick={() => void desktopApps.abrirDownload(d.id)}
                    >
                      <span className="appdl__nome">{d.arquivo}</span>
                      <span className="appdl__meta">
                        {nomeDaApp(d.app)} · {hora(d.quando_ms)}
                        {d.status === 'erro' ? ' · falhou' : ''}
                      </span>
                    </button>
                    <button
                      type="button"
                      className="appdl__revelar"
                      title="Mostrar na pasta"
                      aria-label={`Mostrar ${d.arquivo} na pasta`}
                      disabled={d.status !== 'ok'}
                      onClick={() => void desktopApps.revelarDownload(d.id)}
                    >
                      <i className="fas fa-folder" aria-hidden />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      ) : null}
    </span>
  )
}

/**
 * O que aparece na web quando o usuario clica numa aplicacao.
 *
 * Explica em uma frase por que nao abre aqui e leva ao aplicativo. O cadastro
 * segue valendo — o que ele fez na web estara la quando abrir o desktop.
 */
function ConviteAoDesktop({ app, onCopiarSenha }: { app: UserApp; onCopiarSenha: () => void }) {
  const sistema = sistemaOperacional()

  return (
    <div className="apparea__aviso">
      <i className="fas fa-desktop" aria-hidden />
      <h2>{app.title} abre no Orbind Desktop</h2>
      <p>
        Sistemas como o {hostDe(app.url)} não aceitam ser abertos dentro de uma página de outro endereço — é uma
        proteção do próprio navegador, e vale para qualquer site que tentasse fazer isso. O aplicativo do Orbind não
        tem essa limitação: lá a aplicação abre como se você tivesse aberto direto.
      </p>
      <p className="apparea__aviso-saida">
        O cadastro que você fez aqui já está salvo{app.hasCredentials ? ', inclusive o acesso guardado' : ''}. Instale o
        aplicativo e suas aplicações estarão esperando por você.
      </p>
      <div className="apparea__aviso-acoes">
        <Button variant="primary" onClick={() => window.open(urlDeDownload(sistema), '_self')}>
          <i className="fas fa-download" aria-hidden style={{ marginRight: 8 }} />
          Baixar para {NOME_DO_SISTEMA[sistema]}
        </Button>
        <Button variant="ghost" onClick={() => window.open(app.url, '_blank', 'noopener,noreferrer')}>
          Abrir em nova aba
        </Button>
        {app.hasCredentials ? (
          <Button variant="ghost" onClick={onCopiarSenha}>
            Copiar senha
          </Button>
        ) : null}
      </div>
      <a className="apparea__aviso-link" href="/download">
        Ver todas as versões
      </a>
    </div>
  )
}

function BoasVindas({
  temAlguma,
  noDesktop,
  onNova,
}: {
  temAlguma: boolean
  noDesktop: boolean
  onNova: () => void
}) {
  return (
    <div className="appvazio">
      <i className="fas fa-th-large" aria-hidden />
      <h1>Sua área de trabalho</h1>
      <p>
        {temAlguma
          ? noDesktop
            ? 'Escolha uma aplicação na lista ao lado para abri-la aqui dentro.'
            : 'Escolha uma aplicação na lista ao lado. Para abri-las, use o Orbind Desktop.'
          : 'Cadastre os sites que você mais usa e acesse todos sem sair do Orbind.'}
      </p>
      {temAlguma ? null : (
        <Button variant="primary" onClick={onNova}>
          Adicionar a primeira
        </Button>
      )}
    </div>
  )
}

/** Só o host, que é o que identifica o site numa lista estreita. */
function hostDe(url: string) {
  try {
    return new URL(url).host.replace(/^www\./, '')
  } catch {
    return url
  }
}

